import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Mail, Phone, MapPin, Calendar, Briefcase, FileText, MessageSquare, User, HandCoins, Download, Radio, Clock } from "lucide-react";

const STAGES = {
  recommended: { label: "Recomendado", chip: "chip-blue" },
  interview:   { label: "Entrevista",   chip: "chip-blue" },
  selected:    { label: "Selecionado",  chip: "chip-terra" },
  started:     { label: "Iniciou funções", chip: "chip-green" },
  validated:   { label: "Período mínimo", chip: "chip-green" },
  rejected:    { label: "Rejeitado",    chip: "chip-neutral" },
  withdrawn:   { label: "Desistiu",     chip: "chip-neutral" },
  blacklisted: { label: "Blacklist",    chip: "chip-terra" },
};

function Field({ icon: Icon, label, value, mono = false, children }) {
  if (!value && !children) return null;
  return (
    <div>
      <div className="text-xs eyebrow flex items-center gap-1.5 !text-[var(--ink-soft)]">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`mt-1 text-sm text-[var(--ink)] ${mono ? "font-mono" : ""}`}>
        {children || value}
      </div>
    </div>
  );
}

export default function CandidateDetailModal({ candidateId, onClose }) {
  const [cand, setCand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!candidateId) return;
    setLoading(true);
    api.get(`/candidates/${candidateId}`)
      .then(({ data }) => setCand(data))
      .catch((err) => {
        toast.error(formatApiError(err.response?.data?.detail) || "Erro ao carregar candidato");
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [candidateId, onClose]);

  const downloadCv = () => {
    if (!cand?.cv_data) return;
    const a = document.createElement("a");
    a.href = cand.cv_data;
    a.download = cand.cv_filename || `cv-${cand.name.replace(/\s+/g, "-")}`;
    a.click();
  };

  if (!candidateId) return null;

  const stage = cand ? STAGES[cand.stage] || { label: cand.stage, chip: "chip-neutral" } : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 flex items-start justify-between border-b border-[var(--border)] sticky top-0 bg-white z-10">
          <div>
            <div className="eyebrow">Ficha do candidato</div>
            {cand && (
              <>
                <h2 className="font-heading text-3xl mt-1">{cand.name}</h2>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className={`chip ${stage.chip}`} data-testid="candidate-detail-stage">{stage.label}</span>
                  <span className="chip chip-neutral">Origem: {cand.source}</span>
                </div>
              </>
            )}
          </div>
          <button onClick={onClose} className="pill-btn pill-ghost !py-2 !px-3 flex-shrink-0" data-testid="close-candidate-detail"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-[var(--ink-soft)]">A carregar…</div>
          ) : !cand ? null : (
            <div className="space-y-6">
              {/* Contact */}
              <section>
                <div className="eyebrow mb-3">Contactos</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field icon={Mail} label="Email" value={cand.email}>
                    <a href={`mailto:${cand.email}`} className="underline text-[var(--forest)]" data-testid="candidate-email-link">{cand.email}</a>
                  </Field>
                  <Field icon={Phone} label="Telefone" value={cand.phone}>
                    <a href={`tel:${cand.phone.replace(/\s+/g, "")}`} className="underline text-[var(--forest)]" data-testid="candidate-phone-link">{cand.phone}</a>
                  </Field>
                  <Field icon={MapPin} label="Localidade" value={cand.locality} />
                  <Field icon={Calendar} label="Disponibilidade" value={cand.availability} />
                </div>
              </section>

              {/* Exclusion / blacklist reason */}
              {(cand.exclusion_reason || cand.excluded_at) && (
                <section>
                  <div className="eyebrow mb-3 flex items-center gap-1.5 !text-[var(--terracotta)]">⚠️ Motivo de exclusão</div>
                  <div className="p-4 rounded-xl bg-[rgba(217,108,74,0.08)] border border-[rgba(217,108,74,0.3)] text-sm space-y-1" data-testid="candidate-exclusion-block">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`chip ${cand.stage === "blacklisted" ? "chip-terra" : "chip-neutral"}`}>{STAGES[cand.stage]?.label || cand.stage}</span>
                      {cand.exclusion_category && <span className="chip chip-neutral">{cand.exclusion_category}</span>}
                    </div>
                    <div className="text-[var(--ink)] mt-2">{cand.exclusion_reason}</div>
                    <div className="text-xs text-[var(--ink-soft)] mt-2">
                      {cand.excluded_by && <>por <strong>{cand.excluded_by}</strong></>}
                      {cand.excluded_at && <> · em {new Date(cand.excluded_at).toLocaleString("pt-PT")}</>}
                    </div>
                  </div>
                </section>
              )}

              {/* Experience */}
              <section>
                <div className="eyebrow mb-3">Experiência</div>
                <div className="p-4 rounded-xl bg-[var(--muted)] text-sm whitespace-pre-wrap leading-relaxed" data-testid="candidate-experience">
                  {cand.experience || "—"}
                </div>
              </section>

              {/* CV */}
              {cand.cv_data && (
                <section>
                  <div className="eyebrow mb-3">Currículo</div>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-[var(--muted)] border border-[var(--border)]">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-[var(--forest)] flex-shrink-0"/>
                      <div className="text-sm truncate">{cand.cv_filename || "curriculum.pdf"}</div>
                    </div>
                    <button onClick={downloadCv} className="pill-btn pill-primary !py-2 !px-4 flex-shrink-0" data-testid="candidate-cv-download">
                      <Download className="w-4 h-4"/> Descarregar
                    </button>
                  </div>
                </section>
              )}

              {/* Observations */}
              {cand.observations && (
                <section>
                  <div className="eyebrow mb-3 flex items-center gap-1.5"><MessageSquare className="w-3 h-3"/> Observações</div>
                  <div className="p-4 rounded-xl bg-[rgba(59,113,151,0.08)] border border-[rgba(59,113,151,0.2)] text-sm whitespace-pre-wrap">{cand.observations}</div>
                </section>
              )}

              {/* Vacancy */}
              {cand.vacancy && (
                <section>
                  <div className="eyebrow mb-3 flex items-center gap-1.5"><Briefcase className="w-3 h-3"/> Vaga associada</div>
                  <div className="card-surface p-4">
                    <div className="font-semibold">{cand.vacancy.title}</div>
                    <div className="text-xs text-[var(--ink-soft)] mt-1">{cand.vacancy.locality} · {cand.vacancy.schedule}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`chip ${cand.vacancy.service_type === "continuous" ? "chip-green" : "chip-blue"}`}>
                        {cand.vacancy.service_type === "continuous" ? "🟢 Continuidade" : "🔵 Pontual"}
                      </span>
                      <span className="chip chip-neutral">{cand.vacancy.status}</span>
                    </div>
                  </div>
                </section>
              )}

              {/* Referrer (admin-only) */}
              {cand.referrer && (
                <section>
                  <div className="eyebrow mb-3 flex items-center gap-1.5"><User className="w-3 h-3"/> Referenciador</div>
                  <div className="card-surface p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-semibold">{cand.referrer.full_name}</div>
                        <div className="text-xs text-[var(--ink-soft)]">{cand.referrer.email} · {cand.referrer.phone}</div>
                        <div className="text-xs text-[var(--ink-soft)] mt-1">{cand.referrer.locality}</div>
                      </div>
                      <span className="chip chip-neutral font-mono" data-testid="candidate-referrer-code">{cand.referrer.code}</span>
                    </div>
                  </div>
                </section>
              )}

              {/* Bonus */}
              {cand.bonus && (
                <section>
                  <div className="eyebrow mb-3 flex items-center gap-1.5"><HandCoins className="w-3 h-3"/> Prémio</div>
                  <div className="card-surface p-4 grid sm:grid-cols-4 gap-3 text-sm">
                    <div><div className="text-xs text-[var(--ink-soft)]">Valor</div><div className="font-heading text-2xl text-[var(--terracotta)]">{Number(cand.bonus.amount).toFixed(0)} €</div></div>
                    <div><div className="text-xs text-[var(--ink-soft)]">Contratação</div><div>{(cand.bonus.hire_date || "").slice(0, 10)}</div></div>
                    <div><div className="text-xs text-[var(--ink-soft)]">Pagamento</div><div>{cand.bonus.scheduled_payment_date}</div></div>
                    <div><div className="text-xs text-[var(--ink-soft)]">Estado</div><div><span className="chip chip-neutral">{cand.bonus.status}</span></div></div>
                  </div>
                </section>
              )}

              {/* Timestamps */}
              <section className="pt-4 border-t border-[var(--border)]">
                <div className="grid sm:grid-cols-3 gap-3 text-xs text-[var(--ink-soft)]">
                  <div>Recomendado em <br/><strong className="text-[var(--ink)]">{new Date(cand.created_at).toLocaleString("pt-PT")}</strong></div>
                  {cand.started_at && <div>Início de funções <br/><strong className="text-[var(--ink)]">{new Date(cand.started_at).toLocaleString("pt-PT")}</strong></div>}
                  {cand.validated_at && <div>Validação <br/><strong className="text-[var(--ink)]">{new Date(cand.validated_at).toLocaleString("pt-PT")}</strong></div>}
                </div>
              </section>

              {/* Stage history timeline */}
              {Array.isArray(cand.stage_history) && cand.stage_history.length > 0 && (
                <section className="pt-4 border-t border-[var(--border)]">
                  <div className="eyebrow mb-3 flex items-center gap-1.5"><Clock className="w-3 h-3"/> Histórico de estados</div>
                  <ol className="relative border-l-2 border-[var(--border)] ml-2 space-y-4" data-testid="candidate-stage-history">
                    {cand.stage_history.map((h, i) => {
                      const fromLabel = STAGES[h.from]?.label || h.from;
                      const toLabel = STAGES[h.to]?.label || h.to;
                      const isExclusion = ["rejected", "withdrawn", "blacklisted"].includes(h.to);
                      return (
                        <li key={i} className="ml-4" data-testid={`history-item-${i}`}>
                          <span className={`absolute -left-[7px] w-3 h-3 rounded-full ${isExclusion ? "bg-[var(--terracotta)]" : "bg-[var(--forest)]"} border-2 border-white`}/>
                          <div className="text-xs text-[var(--ink-soft)]">{new Date(h.at).toLocaleString("pt-PT")}</div>
                          <div className="text-sm">
                            <span className="text-[var(--ink-soft)]">{fromLabel}</span>
                            <span className="mx-1.5 text-[var(--ink-soft)]">→</span>
                            <strong className={isExclusion ? "text-[var(--terracotta)]" : "text-[var(--forest)]"}>{toLabel}</strong>
                            {h.by && <span className="text-xs text-[var(--ink-soft)]"> · por {h.by}</span>}
                          </div>
                          {h.exclusion_reason && (
                            <div className="text-xs text-[var(--terracotta)] mt-0.5">
                              {h.exclusion_category ? <><strong>{h.exclusion_category}</strong> — </> : null}{h.exclusion_reason}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
